import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { IGetProjectServiceRes } from '@/api/Sherpa/Project/modules/ProjectCore/types';
import type { IGetProjectTeamMembersServiceRes } from '@/api/Sherpa/Project/modules/ProjectTeam/types';
import { getProjects } from '../../../api/project';
import { getProject, getProjectEvents, getProjectPhases } from '../../../api/Sherpa/Project';
import { getProjectAssets } from '../../../api/Sherpa/Project/modules/ProjectAsset';
import { getProjectTeamMembers } from '../../../api/Sherpa/Project/modules/ProjectTeam';
import { SherpaProjectReactQueryKey } from '../../../constants/ReactQueryKeys';
import { workspaceSelector } from '../../../store';
import type { FiltersInterface, ParamInterface } from '../../../types/Miscellaneous';
import type { QueryParamsInterface } from '../../../types/Shared';

interface IProjectHookProps {
  projectId: number;
  params?: QueryParamsInterface;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}

export const useProject = ({ projectId, params, onSuccess, onError }: IProjectHookProps) => {
  //Tanstack: version 5 query implementation
  return useQuery<IGetProjectServiceRes, any>({
    queryKey: [SherpaProjectReactQueryKey.GET_PROJECT, params, projectId],
    queryFn: () => getProject({ projectId, params }),
    onSuccess,
    onError,
  });
};

interface ProjectQueryInterface {
  params?: ParamInterface | FiltersInterface;
  onSuccess?: (data: any) => void;
  onError?: (err: any) => void;
}

export const useProjectsData = ({ params, onSuccess, onError }: ProjectQueryInterface) => {
  const { currentWorkspace } = useSelector(workspaceSelector);
  return useQuery({
    queryKey: ['get-projects', params, currentWorkspace?.id],
    queryFn: () =>
      getProjects({
        params: { ...params, workspaceId: currentWorkspace?.id },
      }),
    onSuccess,
    onError,
    keepPreviousData: true,
    staleTime: 30000,
  });
};

export const useProjectPhases = () => {
  //Tanstack: version 5 query implementation
  return useQuery({
    queryKey: [SherpaProjectReactQueryKey.GET_PROJECT_PHASES],
    queryFn: getProjectPhases,
    staleTime: 30000,
  });
};

interface ProjectTeamMembersHookProps {
  projectId: number;
  params?: QueryParamsInterface;
  onSuccess?: (data: any) => void;
  onError?: (err: any) => void;
}
export const useProjectTeamMembers = ({ projectId, params, onSuccess, onError }: ProjectTeamMembersHookProps) => {
  //Tanstack: version 5 query implementation
  return useQuery<IGetProjectTeamMembersServiceRes>({
    queryKey: [SherpaProjectReactQueryKey.GET_PROJECT_MEMBERS, params, projectId],
    queryFn: () => getProjectTeamMembers({ projectId, params }),
    onSuccess,
    onError,
    keepPreviousData: true,
    staleTime: 30000,
  });
};

interface IProjectEventsHookProps {
  projectId: number;
  params?: QueryParamsInterface;
  onSuccess?: (data: any) => void;
  onError?: (data: any) => void;
}

export const useProjectEvents = ({ projectId, params, onSuccess, onError }: IProjectEventsHookProps) => {
  //Tanstack: version 5 query implementation
  return useQuery({
    queryKey: [SherpaProjectReactQueryKey.GET_PROJECT_EVENTS, { params, projectId }],
    queryFn: () => getProjectEvents({ projectId, params }),
    onSuccess,
    onError,
    keepPreviousData: true,
    staleTime: 30000,
  });
};

interface IProjectAssetsHookProps {
  projectId: number;
  params?: QueryParamsInterface;
  onSuccess?: (data: any) => void;
  onError?: (data: any) => void;
}

export const useProjectAssets = ({ projectId, params, onSuccess, onError }: IProjectAssetsHookProps) => {
  //Tanstack: version 5 query implementation
  return useQuery({
    queryKey: [SherpaProjectReactQueryKey.GET_PROJECT_ASSETS, { params, projectId }],
    queryFn: () => getProjectAssets({ projectId, params }),
    onSuccess,
    onError,
    keepPreviousData: true,
    staleTime: 30000,
  });
};
